/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import {LeftNav} from '@cchome/leftnav';
import '@cchome/leftnav/module/main.css';
import {SearchBar} from '@ccsearch/search';
import Button from '@react/react-spectrum/Button';
import Icon from '@react/react-spectrum/Icon';
import Close from '@react/react-spectrum/Icon/Close';
import Help from '@react/react-spectrum/Icon/Help';
import {CCHomeAppsCCDUtil, CCHomeAppsUtil, GET_HELP_OPEN_SOURCES, HelpPopoverUtil} from 'cchome-apps';
import {DeviceDetectionUtil} from 'cchome-shared/utils';
import React, {Component, CSSProperties, Fragment, createRef} from 'react';
import ReactDOM from 'react-dom';
import {FormattedMessage, injectIntl} from 'react-intl';
import {connect} from 'react-redux';
import {withRouter} from 'react-router';
import {matchRoutes} from 'react-router-config';
import {Link} from 'react-router-dom';
import uuid from 'uuid';
import getConfig from '../../../config';
import AnalyticsUtil from '../../../utils/AnalyticsUtil';
import LocalStorageUtils from '../../../utils/LocalStorageUtils';
import URLUtils from '../../../utils/Url';
import {setLeftNavOpen, setMainContainerOverlay} from '../../actions/appActions';
import {searchQuery} from '../../actions/searchActions';
import GrowthUtils from '../../containers/Growth/growthUtil';
import SearchHostContext from '../../containers/Search/SearchHostContext';
import ccstorageReducer from '../../reducers/ccstorageReducer';
import UIStore from '../../redux/UIStore';
import {SearchRoutes} from '../../Routes/SearchRoutes';
import {HOME_TAB, LIBRARIES_TAB, linksLearnEntitle, SEARCH_TAB, STOCK_MP_TAB} from '../TopNav/constants';
import TopNav from '../TopNav/TopNav';
import CCIcon from './CCIcon';
import './Navbar.scss';
import Utilnav from './Utilnav6';

const leftRailActiveRoutes = ['/cc', '/cc/canvas', '/cc/illustrator', '/cc/photoshop', '/apps', '/cc/discover'];
const leftRailInactiveRoutes = ['/cc/libraries'];
const overlayRoutes = ['/apps', '/cc/discover'];

/**
 * A Navbar.
 * @class
 */

interface Props {
	history: any;
	setLeftNavOpen: Function;
	setMainContainerOverlay?: Function;
	leftNavOpen?: boolean;
	forceHideMenu?: boolean;
	forceHideNavbar?: boolean;
	locale?: any;
	routes?: Array<Object>;
	location?: any;
	intl: any;
	subscriptionInfo?: any;
	profileFetchError?: boolean;
	isAllAppsStarter?: boolean;
	searchHistoryItems?: Array<string>;
	searchQuery?: Function;
	searchQueryValue?: string;
	isLoggedOutExp?: boolean;
	isLoggedOutCampaignExp?: boolean;
	environment?: string;
	isMobilePhotoPlan?: boolean;
	isNUJTestWorkflow?: boolean;
	leftRailOpen: boolean;
	setLeftRail: (newstate: boolean) => any;
	language: string;
}

interface State {
	breakpoint: string;
	searchTakeoverEnabled: boolean;
	lastNonSearchRoute: string;
	routeHasOverlay: boolean;
}

class Navbar extends Component<Props, State> {
	// this searchBar should be HTMLElement, however flow has limitation to detect this so setting as any for now.
	searchBar: any;
	searchBarDropdown: any;
	externalLeftNavRef: any;

	constructor(props) {
		super(props);

		const ls = new LocalStorageUtils();
		const leftRailOpen = ls.getItem('left-rail-open') === 'true' || false;

		ls.setItem('left-rail-open', leftRailOpen);

		this.state = {
			breakpoint: window.cchome.util.currentBreakpoint,
			searchTakeoverEnabled: false,
			lastNonSearchRoute: '',
			routeHasOverlay: false,
		};

		const searchHostContext = new SearchHostContext();
		searchHostContext.setHistory(this.props.history);
		this.externalLeftNavRef = createRef();
	}

	toggleLeftRail = (e) => {
		e.preventDefault();
		this.props.setLeftRail(!this.props.leftRailOpen);
	};

	/**
	 * [getCurrentQuery gets the current query from url]
	 * @returns {[array]}
	 */
	getCurrentQuery() {
		let currentQuery = '';
		const hasQuery = location.search.indexOf('?query') > -1;

		if (hasQuery) {
			currentQuery = decodeURIComponent(location.search.split('&')[0].replace('?query=', ''));
		}

		return currentQuery;
	}

	handleBreakpointChange = (e: CustomEvent) => {
		if (!e.detail) {
			return;
		}
		const breakpoint = e.detail.name;

		if (
			breakpoint === 'S'
			|| breakpoint === 'MS'
			|| breakpoint === 'M'
			|| breakpoint === 'LM'
			|| breakpoint === 'L'
		) {
			this.setState({breakpoint});
		}
		else {
			this.setState({breakpoint});
		}
	};

	componentDidMount() {
		window.addEventListener('breakpointChanged', e => this.handleBreakpointChange(e as CustomEvent));
		if (this.props.searchQuery) {
			this.props.searchQuery(this.getCurrentQuery());
		}
		// const searchRollOutBucket = window.cchome.util.testBucket20190430CCSearchRollout();

		// Enable Search Quick Links Experiment
		window.cchome.util.testBucket20190916SearchQuickLinksTest();

		window.addEventListener('mousedown', this.handleSearchMouseClick);
		window.addEventListener('keydown', this.handleKeydownEvent);
		if (this.props.routes && this.isSearchRoute()) {
			this.setState({searchTakeoverEnabled: true});
			this.toggleMainContainerOverlay(true);
		}

		// this will enable other components(e.g. Home Component) in cchome to enable search bar without click on it directly
		window.addEventListener('enableSearchBar', this.onSearchIconClick);
		window.addEventListener('resize', () => {
			if (document.body.clientWidth < 960 && this.props.leftRailOpen) {
				this.setState({routeHasOverlay: true});
			}
			else {
				const routeHasOverlay = overlayRoutes.reduce(
					(acc, route) => acc || location.href.indexOf(route) !== -1,
					false,
				);
				this.setState({routeHasOverlay});
			}
		});
	}

	componentDidUpdate(prevProps) {
		if (this.props.searchQuery) {
			this.props.searchQuery(this.getCurrentQuery());
		}

		/* istanbul ignore next */
		// If we are transitioning from a search to a non-search route, exit out of the search takeover mode.
		if (this.props.routes && this.isSearchRoute(prevProps.location.pathname) && !this.isSearchRoute()) {
			if (this.state.searchTakeoverEnabled) {
				// eslint-disable-next-line react/no-did-update-set-state
				this.setState({searchTakeoverEnabled: false});
			}
			window.cchome.util.enableHTMLScroll();
			this.toggleMainContainerOverlay(false);
		} // If we are transitioning to a search route, then put us in search takeover mode.
		else if (this.props.routes && this.isSearchRoute()) {
			if (!this.state.searchTakeoverEnabled) {
				// eslint-disable-next-line react/no-did-update-set-state
				this.setState({searchTakeoverEnabled: true});
			}
			window.cchome.util.disableHTMLScroll();
			this.toggleMainContainerOverlay(true);
		}
		else if (this.state.searchTakeoverEnabled) {
			if (!(document.activeElement && document.activeElement.id === 'search-input')) {
				window.cchome.util.disableHTMLScroll();
				this.searchBar.click();
			}
		}
	}

	componentWillUnmount() {
		window.removeEventListener('breakpointChanged', e => this.handleBreakpointChange(e as CustomEvent));
		window.removeEventListener('mousedown', this.handleSearchMouseClick);
		window.removeEventListener('keydown', this.handleKeydownEvent);
		window.removeEventListener('enableSearchBar', this.onSearchIconClick);
	}

	getPrompt() {
		const prompt = 'app.search.placeholder.search';
		AnalyticsUtil.searchPromptId = prompt;
		return this.props.intl.formatMessage({id: prompt});
	}

	onSearchBarSubmit(e) {
		if (typeof e === 'string' && e !== '') {
			if (this.props.routes && !this.isSearchRoute()) {
				this.setState({lastNonSearchRoute: `${this.props.location.pathname}${this.props.location.search}`});
			}
			this.props.history.push(`${SearchRoutes[0].path}?query=${encodeURIComponent(e)}`);
			if (this.props.searchQuery) {
				this.props.searchQuery(e);
			}
		}
	}
	// END NEW SEARCH BAR METHODS

	/**
	 * Checks to see if the current route is the Search route.
	 * If no pathname is specified, the current pathname is used.
	 * REMEMBER TO CHECK this.props.routes exists before using this method.
	 */
	isSearchRoute(pathname?: string) {
		let path = this.props.history.location.pathname;
		if (pathname) {
			path = pathname;
		}
		const matches = matchRoutes(this.props.routes, path);
		let tab = '';
		if (matches && matches.length > 0) {
			tab = matches[0].route.tab;
		}
		return tab === SEARCH_TAB;
	}

	isWebappsRoute(pathname?: string) {
		let path = this.props.history.location.pathname;
		if (pathname) {
			path = pathname;
		}
		const matches = matchRoutes(this.props.routes, path);
		let isWebapps;
		// TODO: Need to change this logic
		if (matches && matches.length > 0) {
			isWebapps
				= matches[0].match.path.includes('/cc/canvas')
				|| matches[0].match.path.includes('/cc/photoshop')
				|| matches[0].match.path.includes('/cc/illustrator');
		}
		return isWebapps;
	}

	/**
	 * Handles search bar mouse click events
	 * @param {object} e event object
	 */
	handleSearchMouseClick = (e) => {
		// Enable the search takeover when the user clicks into the search bar
		const isClearButton
			= e
			&& e.target
			&& (e.target.className === 'spectrum-ClearButton' || e.target.className === 'searchbar-clear-button');
		if (!isClearButton && this.searchBar && e && e.target && this.searchBar.contains(e.target)) {
			if (!this.state.searchTakeoverEnabled) {
				this.setState({searchTakeoverEnabled: true});
				this.toggleMainContainerOverlay(true);
			}
		} // Disable the search takeover if the user clicks anywhere outside of the search bar (while in a non-search route)
		else if (!isClearButton && this.state.searchTakeoverEnabled && this.props.routes && !this.isSearchRoute()) {
			this.onClickSearchExit();
		}
		else if (isClearButton) {
			// If user clicks on clear button outside of take over, wait for the text to clear, then open the search over
			if (!this.state.searchTakeoverEnabled) {
				setTimeout(() => {
					this.setState({searchTakeoverEnabled: true});
					this.toggleMainContainerOverlay(true);
				}, 200);
			}
		}
	};

	/**
	 * Handles keydown events
	 *  @param {object} e event object
	 */
	handleKeydownEvent = (e) => {
		// Disable the search takeover if the user presses the Escape key
		if (e.keyCode === 27 || e.key === 'Escape') {
			this.onClickSearchExit();
		} // Fix for JIRA https://jira.corp.adobe.com/browse/CCS-65
		// Fix the tabbing behavior for the search takeover
		else if (
			(e.key === 'Tab' || e.which === 9 || e.keyCode === 9)
			&& this.state.searchTakeoverEnabled
			&& this.props.routes
			&& !this.isSearchRoute()
		) {
			const isFirefox = DeviceDetectionUtil.isFirefox();

			// This function returns the first focussable element from the Footer.
			const getFirstFocussableFooterElement = () => {
				let footerElement: HTMLElement | null = null;

				const footer = document.getElementsByClassName('footer-container');

				if (footer && footer[0]) {
					const focusableChildren: any = footer[0].querySelectorAll(
						'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
					);

					if (focusableChildren && focusableChildren[0]) {
						footerElement = focusableChildren[0];
					}
				}
				return footerElement;
			};

			// SHIFT + TAB (to navigate elements in the reverse order) - if the element in focus is the first element in the footer or the Jarvis CTA (on Firefox)
			// pass focus back to the exit search button when in takeover mode.
			// Firefox doesn't respect the tabindex values on <a> elements and doesn't include them in the tab order by default so we use the Jarvis CTA instead.
			// See https://www.alexlande.com/articles/cross-browser-tabindex-woes/
			if (e.shiftKey) {
				const element = isFirefox ? document.getElementById('adbmsgCta') : getFirstFocussableFooterElement();

				if (element && document.activeElement && element.isEqualNode(document.activeElement)) {
					const searchExitButton = document.getElementsByClassName(
						'search-exit-button-visible',
					) as HTMLCollectionOf<HTMLElement>;

					if (searchExitButton && searchExitButton[0]) {
						e.preventDefault();
						searchExitButton[0].focus();
					}
				}
			} // TAB - if we have reached the search exit button, skip the other focusable elements (they should be behind the overlay) and instead pass
			// focus to the first focussable child of the footer or the Jarvis CTA (on Firefox).
			else {
				if (
					document.activeElement
					&& document.activeElement.id
					&& document.activeElement.id.includes('search-exit-button')
				) {
					const nextElement = isFirefox
						? document.getElementById('adbmsgCta')
						: getFirstFocussableFooterElement();

					if (nextElement) {
						e.preventDefault();
						nextElement.focus();
					}
				}
			}
		}
	};

	/**
	 * Handles search exit click events
	 */
	onClickSearchExit() {
		const customEvent = new CustomEvent('onSearchExit');
		window.dispatchEvent(customEvent);
		// this.searchBar.onExit();
		this.setState({searchTakeoverEnabled: false});
		if (this.props.searchQuery) {
			this.props.searchQuery('');
		}
		this.toggleMainContainerOverlay(false);
		if (this.props.routes && this.isSearchRoute()) {
			if (this.state.lastNonSearchRoute) {
				this.props.history.push(this.state.lastNonSearchRoute);
				this.setState({lastNonSearchRoute: ''});
			}
			else {
				this.props.history.push('/cc');
			}
		}
		window.cchome.util.enableHTMLScroll();
	}

	getSearchBar() {
		// First, create the hostContext.
		const searchHostContext = new SearchHostContext();

		// Add the feature flag to enable the new SRP, if the user falls into the test bucket
		// and if the flag is not already enabled.
		// Enable Search Auto-Complete for users that fall into the "testA" bucket or "testB" bucket for the new SRP test.
		const featureFlags = window.cchome.util.getAllFeatures();
		if (
			window.cchome.util.testBucket20190430CCSearchRollout() === 'testA'
			|| window.cchome.util.testBucket20190430CCSearchRollout() === 'testB'
		) {
			if (featureFlags.indexOf('temp_enable_new_srp') === -1) {
				featureFlags.push('temp_enable_new_srp');
			}
			if (featureFlags.indexOf('ccsearch_enable_uss_contextual_parameters') === -1) {
				featureFlags.push('ccsearch_enable_uss_contextual_parameters');
				window.cchome.util.testBucket20190815USSContextualParametersTest();
			}
			if (featureFlags.indexOf('temp_enable_search_auto_suggestion_server_uss') === -1) {
				featureFlags.push('temp_enable_search_auto_suggestion_server_uss');
			}
			// Enable USS Auto-Complete Contextual Parameters
			if (featureFlags.indexOf('ccsearch_enable_uss_auto_complete_contextual_parameters') === -1) {
				featureFlags.push('ccsearch_enable_uss_auto_complete_contextual_parameters');
			}
			// Enable UI Intervention/Knowledge card for new SRP
			window.cchome.util.testBucket20190606UIinterventionTest();

			// Enable Search Quick Links Experiment
			window.cchome.util.testBucket20190916SearchQuickLinksTest();
			// add ff for max
			if (window.cchome.util.hasFeature('2019MAX_Permanent')) {
				featureFlags.push('2019MAX_Permanent');
			}
		}

		// Enable UI Intervention/Knowledge card for new SRP
		if (window.cchome.util.hasFeature('temp_enable_ui_intervention')) {
			featureFlags.push('temp_enable_ui_intervention');
		}

		searchHostContext.setFeatureFlags(featureFlags);

		let searchBar;
		if (this.isWebappsRoute()) {
			searchBar = null;
		}
		else {
			searchBar = (
				<SearchBar
					key={this.props.searchQueryValue}
					ref={(component) => {
						this.searchBar = ReactDOM.findDOMNode(component);
					}}
					placeholder={this.getPrompt()}
					maxLength={'100'}
					onSubmit={e => this.onSearchBarSubmit(e)}
					initialValue={this.getCurrentQuery()}
					aria-label={this.props.intl.formatMessage({id: 'app.navbar.search.aria.label'})}
					searchHostContext={searchHostContext}
				/>
			);
		}
		return searchBar;
	}

	toggleMenu = () => {
		if (this.props.setLeftNavOpen) {
			this.props.setLeftNavOpen(!this.props.leftNavOpen);
		}
	};

	onMenuIconKeyDown(event) {
		/* istanbul ignore else */
		if (event.key === ' ' || event.key === 'Enter' || event.which === 13 || event.which === 32) {
			if (this.props.setLeftNavOpen) {
				this.props.setLeftNavOpen(!this.props.leftNavOpen);
			}
		}
	}

	onSearchIconKeyDown(event) {
		/* istanbul ignore else */
		if (event.key === ' ' || event.key === 'Enter' || event.which === 13 || event.which === 32) {
			this.setState({searchTakeoverEnabled: true});
			this.toggleMainContainerOverlay(true);
		}
	}

	onSearchIconClick = () => {
		this.setState({searchTakeoverEnabled: true});
		this.toggleMainContainerOverlay(true);
	};

	/**
	 * This method will set a prop to add an overlay on the main container.
	 * This overlay is only added when the search takeover is in effect and only on non-Search routes.
	 */
	toggleMainContainerOverlay(searchTakeoverEnabled) {
		if (this.props.routes && !this.isSearchRoute() && searchTakeoverEnabled) {
			if (this.props.setMainContainerOverlay) {
				this.props.setMainContainerOverlay(true);
			}
		}
		else {
			if (this.props.setMainContainerOverlay) {
				this.props.setMainContainerOverlay(false);
			}
		}
	}

	/**
	 * Indicates whether non-search items should be hidden to support search takeover.
	 * @returns boolean
	 */
	hideItemsForExpandedSearch() {
		return this.state.searchTakeoverEnabled;
	}

	renderCCTitleForAppPicker() {
		const title = <FormattedMessage id={'app.navbar.title.appPicker'} />;
		const ariaLabel = this.props.intl ? this.props.intl.formatMessage({id: 'app.navbar.title.appPicker'}) : '';
		return (
			<span
				className='navbar-title-link navbar-title-span navbar-apps-picker'
				tabIndex={0}
				aria-label={ariaLabel}
			>
				<div className='logo'>{this.renderIcon()}</div>
				<div className='navbar-title appPicker-navbar-title'>
					<h1 className='title'>{title}</h1>
				</div>
			</span>
		);
	}

	renderGrowthThroughlineHelp() {
		const guid = uuid.v4();
		const analyticParam = `x-product=CCHome&x-product-location=throughline&guid=${guid}`;
		const ingestUrl = `https://helpx.adobe.com/contact.html?${analyticParam}`;
		return (
			<div className='growthThroughlineHelpDiv'>
				<a
					target='_blank'
					rel='noreferrer'
					href={ingestUrl}
					onClick={() => {
						const analytics = new AnalyticsUtil();
						analytics.sendGrowthThroughlineHelpAnalytics(ingestUrl);
					}}
				>
					<Help size='M' />
				</a>
			</div>
		);
	}

	renderIcon = () => {
		let data: any = null;
		const path = getConfig('SERVICES').searchApp.productsPath.concat(`?locale=${window.cchome.util.getLocale()}`);

		if (sessionStorage && sessionStorage.getItem(path)) {
			data = sessionStorage.getItem(path);
			try {
				data = JSON.parse(data);
			}
			catch (e) {
				// do nothing
			}
		}

		const currentPath = window.location.href;
		let icon;
		if (currentPath.indexOf('/cc/photoshop') >= 0) {
			icon = (
				<img
					className='cc-branding-icon'
					src={`https://${window.location.host}/assets/common/ps_beta_appicons_noshadow_96.png`}
				/>
			);
		}
		else if (currentPath.indexOf('/cc/illustrator') >= 0) {
			// icon = <img className="app-logo-img" src={data.apps['ILST'].iconUrls['96']} />;
			icon = (
				<img
					className='cc-branding-icon'
					src={`https://${window.location.host}/assets/common/ai_beta_appicons_256.svg`}
				/>
			);
		}
		else {
			icon = <CCIcon />;
		}
		return icon;
	};

	/**
	 * Renders the search bar with takeover behavior.
	 */
	renderSearchBarTakeover() {
		return (
			<div className={this.state.searchTakeoverEnabled ? 'search-bar-takeover' : 'search-bar-no-takeover'}>
				{this.getSearchBar()}
				<Button
					className={
						this.state.searchTakeoverEnabled ? 'search-exit-button-visible' : 'search-exit-button-hidden'
					}
					label={this.props.intl.formatMessage({id: 'app.navbar.exit.search'})}
					icon={<Close />}
					id={'search-exit-button'}
					onClick={() => this.onClickSearchExit()}
					quiet
					variant='primary'
				/>
			</div>
		);
	}

	/**
	 *
	 * @param {string} title nav bar title.
	 */
	renderWithSearchTakeover(title) {
		const searchIconTestClass = 'searchRegular';

		const navBarInlineStyle: CSSProperties = {position: 'fixed'};

		const featureFlags = window.cchome.util.getAllFeatures();

		const routeIsActive = leftRailActiveRoutes.reduce(
			(acc, route) => acc || location.pathname.startsWith(route),
			false,
		);

		const routeIsNotInactive = leftRailInactiveRoutes.reduce(
			(acc, route) => acc && location.pathname.startsWith(route),
			true,
		);

		const excludedPath
			= GrowthUtils.isGrowthWorkflow()
			|| this.props.isNUJTestWorkflow
			|| this.props.isAllAppsStarter
			|| this.props.isMobilePhotoPlan
			|| this.props.isNUJTestWorkflow
			|| CCHomeAppsUtil.isCCHInLMS();

		const routeHasOverlay
			= overlayRoutes.reduce((acc, route) => acc || location.href.indexOf(route) !== -1, false)
			|| document.body.clientWidth < 960;

		const includesLeftNav
			= window.cchome.util.hasFeature('apphomes')
			&& !window.cchome.util.isIE()
			&& routeIsActive
			&& !routeIsNotInactive
			&& !excludedPath;

		const handleNavigate = (url: string | undefined) => {
			// e.preventDefault();
			this.props.history.push(url);
		};

		const postExternalLeftNavRef = (url: string | undefined) => {
			if (url && this.externalLeftNavRef && this.externalLeftNavRef.current) {
				this.externalLeftNavRef.current.postExternalLeftNavLink(url);
			}
		};

		const handleChangeUrl = (url: string | undefined) => {
			// e.preventDefault();
			window.location.href = url || '';
		};

		const toggleCallback = (newstate: boolean) => {
			this.props.setLeftRail(newstate);
		};

		const isDiscover = location.pathname.startsWith('/cc/discover');
		const isHome = location.pathname.startsWith('/cc') && !isDiscover;
		const isAppHome
				= location.pathname.startsWith('/cc/canvas')
				|| location.pathname.startsWith('/cc/photoshop')
				|| location.pathname.startsWith('/cc/illustrator');
		const isMobile = document.body.clientWidth < 960;
		const isOverlayRoute = !isHome && !isAppHome;
		const defaultOpen = !isMobile && !isOverlayRoute;

		const displayIconStyle = this.isWebappsRoute() ? 'none' : 'block';
		const enableLogger = window.cchome.util.hasFeature('enable_logger');

		// For mobile banner being open
		const hasTopBanner
			= window.cchome.util.isMobileOrTabletBrowser()
			&& !(localStorage && localStorage.getItem('dismissedCCMobileBanner'));
		// Must do this storage check rather than document.class check
		// b/c document.class happens too late
		let leftNavContainerClassname = 'leftnav-host-container';
		if (hasTopBanner && isHome && !isAppHome) {
			// hasTopBanner && isHome && !isAppHome
			leftNavContainerClassname += ' with-top-banner';
		}

		const imsProfile = window.adobeIMS.getUserProfile();
		let countryCode = 'US';
		if (imsProfile && imsProfile.countryCode) {
			countryCode = imsProfile.countryCode;
		}

		return (
			<div className={`${this.props.forceHideNavbar ? 'hidden' : 'navbar'}`} style={navBarInlineStyle} role='region' aria-label='Top Navigation Bar'>
				{includesLeftNav && (
					<LeftNav
						overlay={routeHasOverlay}
						open={defaultOpen}
						location={location}
						env={this.props.environment}
						locale={this.props.locale}
						featureFlags={featureFlags}
						enableLogger={enableLogger}
						imsData={{
							accessToken: window.adobeIMS.getAccessToken(),
							adobeId: window.cchome.util.getUserId(),
							countryCode,
						}}
						apiKey='CCHomeWeb1'
						ref={this.externalLeftNavRef}
						handleNavigate={handleNavigate}
						toggleCallback={toggleCallback}
						className={leftNavContainerClassname}
						externalLeftNav={
							((
								<>
									<span className='app-title'>
										{this.renderIcon()}
										<div className='title'>{title}</div>
									</span>
									{linksLearnEntitle.map((item, index) => {
										// Hide stock and marketplace tab under ff
										if (
											!window.cchome.util.hasFeature('topnav_stock')
											&& item.tab === STOCK_MP_TAB
										) {
											return null;
										}

										let navigationHandler = item.path
											? () => {
												handleNavigate(item.path);
												postExternalLeftNavRef(item.path);
											}
											: undefined;

										if (!navigationHandler) {
											navigationHandler = item.url ? () => handleChangeUrl(item.url) : undefined;
										}

										if (!navigationHandler || item.tab === LIBRARIES_TAB) {
											return null;
										}

										let label = this.props.intl.formatMessage({id: item.title});

										if (item.tab === HOME_TAB && document.body.clientWidth < 960) {
											label = 'Your Creative Cloud';
										}

										let selected = item.path ? location.href?.includes(item.path) : false;
										if (this.isWebappsRoute()) {
											selected = false;
										}

										if (location.href?.includes('/cc/discover')) {
											selected = item.path === '/cc/discover';
										}

										return (
											<Button
												className='lighter-spectrum-button'
												key={index}
												value={`button-${index}`}
												variant='action'
												quiet
												onClick={navigationHandler}
												selected={selected}
											>
												{label}
											</Button>
										);
									})}
								</>
							) as unknown) as Component
						}
					/>
				)}

				<div
					aria-label='Creative Cloud, link'
					className={this.hideItemsForExpandedSearch() ? 'hidden' : 'nav-header'}
				>
					{!includesLeftNav && (
						<div
							className={this.props.forceHideMenu ? 'menu force-menu' : 'menu'}
							onClick={this.toggleMenu}
							onKeyDown={e => this.onMenuIconKeyDown(e)}
							role='button'
							tabIndex={0}
						>
							<svg
								focusable='false'
								xmlns='http://www.w3.org/2000/svg'
								data-name='S_ShowMenu_18_N@1x'
								height='18'
								id='S_ShowMenu_18_N_1x'
								viewBox='0 0 18 18'
								width='18'
							>
								<defs>
									<style>{'.fill-gray {fill: #767676;}'}</style>
								</defs>
								<rect id='ToDelete' fill='#ff13dc' opacity='0' width='18' height='18' />
								<rect className='fill-gray' height='2' rx='0.5' ry='0.5' width='14' x='2' y='8' />
								<rect className='fill-gray' height='2' rx='0.5' ry='0.5' width='14' x='2' y='3' />
								<rect className='fill-gray' height='2' rx='0.5' ry='0.5' width='14' x='2' y='13' />
							</svg>
						</div>
					)}

					{this.renderCCTitleForAppPicker()}

					<Link
						className='navbar-title-link'
						to={this.isWebappsRoute() ? '#' : '/cc'}
						onClick={() => {
							const analytics = new AnalyticsUtil();
							analytics.sendNavBarLogoClickAnalytics({type: 'click'});
						}}
						tabIndex={0}
					>
						<div className='logo'>{this.renderIcon()}</div>
						<div className={`navbar-title${this.props.isMobilePhotoPlan ? ' appPicker-navbar-title' : ''}`}>
							<h1 className='title'>{title}</h1>
						</div>
					</Link>
				</div>

				{!this.props.isAllAppsStarter && !this.props.isMobilePhotoPlan ? (
					<TopNav
						routes={this.props.routes}
						pathName={this.props.location.pathname}
						hidden={this.hideItemsForExpandedSearch()}
					/>
				) : null}

				<div className={searchIconTestClass}>
					<div className={`nav-main ${this.state.searchTakeoverEnabled ? 'search-takeover' : ''}`}>
						<div className='action-item'>
							<div className='action-regular-takeover'>{this.renderSearchBarTakeover()}</div>
							<div className={this.hideItemsForExpandedSearch() ? 'hidden' : 'action-icon'}>
								<div
									className='search-icon'
									tabIndex={0}
									role='button'
									onClick={() => this.onSearchIconClick()}
									onKeyDown={e => this.onSearchIconKeyDown(e)}
									style={{display: displayIconStyle}}
								>
									<Icon size='S'>
										<svg
											xmlns='http://www.w3.org/2000/svg'
											height='18'
											id='S_Search_18_N'
											viewBox='0 0 18 18'
											width='18'
										>
											<path d='M16.586,15.108,12.7,11.222A6.418,6.418,0,1,0,11.221,12.7l3.886,3.886a1.048,1.048,0,0,0,1.479-1.478ZM3,7.5A4.5,4.5,0,1,1,7.5,12,4.5,4.5,0,0,1,3,7.5Z' />
										</svg>
									</Icon>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className='UtilNavWrapper'>
					<Utilnav
						profileFetchError={this.props.profileFetchError}
						customerSegment={this.props.subscriptionInfo.customerSegment}
						locale={this.props.locale}
						history={this.props.history}
						hidden={this.hideItemsForExpandedSearch()}
						isAllAppsStarter={this.props.isAllAppsStarter}
						isMobilePhotoPlan={this.props.isMobilePhotoPlan}
						isNUJTestWorkflow={this.props.isNUJTestWorkflow}
						isWebappsRoute={this.isWebappsRoute()}
						routes={this.props.routes}
						location={this.props.location}
						intl={this.props.intl}
						isGrowthWorkflow={false}
						shouldRenderGetHelpWidgetForCampaign={this.shouldRenderGetHelpWidgetForCampaign}
					/>
				</div>
			</div>
		);
	}

	shouldRenderGetHelpWidgetForCampaign() {
		return (
			!window.cchome.util.isMobileOrTabletBrowser()
			&& !DeviceDetectionUtil.isMobileOrTab()
			&& !(URLUtils.getParam('workflow') === 'open-file' && URLUtils.getParam('file'))
			&& !(URLUtils.getParam('workflow') === 'route-to-path')
		);
	}

	renderNavBarLoggedOutExp(title, navBarInlineStyle) {
		const {isLoggedOutCampaignExp, location} = this.props;
		const {adobeIMS, cchome} = window;
		const isLoggedIn = adobeIMS && adobeIMS.isSignedInUser();
		const onClick = (e) => {
			const analytics = new AnalyticsUtil();
			let eventData;
			if (isLoggedOutCampaignExp) {
				eventData = {
					workflow: 'Campaign CTA',
					subtype: 'cclogo',
					envSvcName: 'Email Campaign',
					envSvcVersion: URLUtils.getParam('campaignKey'),
					uiViewType: 'Email Campaign',
					type: undefined,
					eventSubcategory: CCHomeAppsCCDUtil.getCampaignWorkflowSubcategory(),
				};
				if (this.shouldRenderGetHelpWidgetForCampaign()) {
					if (HelpPopoverUtil.openGetHelpPopover) {
						eventData.type = 'click';
						HelpPopoverUtil.openGetHelpPopover(GET_HELP_OPEN_SOURCES.LOGGED_OUT_CAMPAIGN_CCLOGO_CLICK);
					}
				}
			}
			analytics.sendNavBarLogoClickAnalytics(eventData);
			e.preventDefault();
			if (!isLoggedOutCampaignExp) {
				if (
					!isLoggedIn
					&& location.pathname.split('/')[3] === 'creative-cloud'
					&& cchome.util.hasFeature('temp_cch_8523_trailhead')
					&& !cchome.util.hasFeature('temp_cc_download_page')
				) {
					// @ts-ignore
					window.location = '/cc';
				}
				else {
					// @ts-ignore
					window.location = '/';
				}
			}
		};

		const renderTitle = () => (
			<>
				<div className='logo'>{this.renderIcon()}</div>
				<div className='navbar-title'>
					<h1 className='title'>{title}</h1>
				</div>
			</>
		);

		return (
			<div className='navbar loggedOutExp' style={navBarInlineStyle}>
				<div aria-label='Creative Cloud, link' className='nav-header'>
					{isLoggedOutCampaignExp ? (
						<span className='navbar-title-link navbar-title-no-click' onClick={onClick}>
							{renderTitle()}
						</span>
					) : (
						<Link className='navbar-title-link' to='/' onClick={onClick}>
							{renderTitle()}
						</Link>
					)}
				</div>
				<div className='UtilNavWrapper'>
					<Utilnav
						locale={this.props.locale}
						history={this.props.history}
						isLoggedOutExp={this.props.isLoggedOutExp}
						isLoggedOutCampaignExp={this.props.isLoggedOutCampaignExp}
						routes={this.props.routes}
						location={this.props.location}
						intl={this.props.intl}
						isGrowthWorkflow={false}
						shouldRenderGetHelpWidgetForCampaign={this.shouldRenderGetHelpWidgetForCampaign}
					/>
				</div>
			</div>
		);
	}

	renderNavBarGrowth(title) {
		const navBarInlineStyle = {position: 'fixed'} as React.CSSProperties;
		const growthConfig = GrowthUtils.getGrowthConfig();
		const navbarClass
			= growthConfig && growthConfig.navbar && growthConfig.navbar.navbarClass
				? ` ${growthConfig.navbar.navbarClass}`
				: '';
		return (
			<div className={`navbar${navbarClass}`} style={navBarInlineStyle}>
				<div aria-label='Creative Cloud, link' className='nav-header'>
					<Link
						className='navbar-title-link'
						to='/cc'
						onClick={() => {
							const analytics = new AnalyticsUtil();
							analytics.sendNavBarLogoClickAnalytics({type: 'click'});
						}}
					>
						<div className='logo'>{this.renderIcon()}</div>
						<div className='navbar-title'>
							<h1 className='title'>{title}</h1>
						</div>
					</Link>
				</div>
				<div className='UtilNavWrapper'>
					{GrowthUtils.isThroughlineWorkflow() && this.renderGrowthThroughlineHelp()}
					<Utilnav
						profileFetchError={this.props.profileFetchError}
						customerSegment={this.props.subscriptionInfo.customerSegment}
						locale={this.props.locale}
						history={this.props.history}
						routes={this.props.routes}
						location={this.props.location}
						intl={this.props.intl}
						isGrowthWorkflow
						shouldRenderGetHelpWidgetForCampaign={this.shouldRenderGetHelpWidgetForCampaign}
					/>
				</div>
			</div>
		);
	}

	render() {
		const {isLoggedOutExp, isLoggedOutCampaignExp, isMobilePhotoPlan} = this.props;
		let title = this.props.intl.formatMessage({
			id: `app.navbar.title${isMobilePhotoPlan || isLoggedOutCampaignExp ? '.appPicker' : ''}`,
		});

		const currentPath = window.location.href;
		if (currentPath.indexOf('/cc') >= 0) {
			if (currentPath.indexOf('/cc/canvas') >= 0) {
				title = this.props.intl.formatMessage({id: 'app.leftnav.fred'});
			}
			else if (currentPath.indexOf('/cc/photoshop') >= 0) {
				title = this.props.intl.formatMessage({id: 'app.leftnav.photoshop'});
			}
			else if (currentPath.indexOf('/cc/illustrator') >= 0) {
				title = this.props.intl.formatMessage({id: 'app.leftnav.illustrator'});
			}
		}

		const navBarInlineStyle: genericObject = {position: 'fixed'};
		if (location.pathname.split('/')[1] === 'quick-actions') {
			// $FlowFixMe
			navBarInlineStyle.display = 'flex';
		}
		let navBarJSX: JSX.Element | null = null;
		if (isLoggedOutExp) {
			navBarJSX = this.renderNavBarLoggedOutExp(title, navBarInlineStyle);
		}
		else if (GrowthUtils.isGrowthWorkflow()) {
			navBarJSX = this.renderNavBarGrowth(title);
		}
		else {
			navBarJSX = this.renderWithSearchTakeover(title);
		}

		return navBarJSX;
	}
}

const mapStateToProps = ({app, profile, search, uistore}) => ({
	leftNavOpen: app.leftNavOpen,
	forceHideMenu: app.forceHideMenu,
	forceHideNavbar: app.forceHideNavbar,
	subscriptionInfo: profile.subscriptionInfo,
	profileFetchError: profile.profileTimeoutOrError,
	searchHistoryItems: search.searchHistoryItems,
	searchQueryValue: search.searchQuery,
	leftRailOpen: uistore.leftRailOpen,
	language: app.language,
});

const mapDispatchToProps = {
	setLeftRail: UIStore.setLeftRail,
	setLeftNavOpen,
	setMainContainerOverlay,
	searchQuery,
};

export default injectIntl(
	// @ts-ignore
	withRouter(connect(mapStateToProps, mapDispatchToProps)(Navbar)),
);
